import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import FolderSharedRounded from "@mui/icons-material/FolderSharedRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import { useTranslation } from "react-i18next";
import { db } from "services/firebase";
import HelpVideo from "components/common/HelpVideo";
import patientVideo from "assets/help/patient-walkthrough.mp4";
import patientCaptions from "assets/help/patient-walkthrough.vtt";
import patientPoster from "assets/help/patient-walkthrough-poster.jpg";

// Titles and descriptions are "home" namespace keys, translated at render.
const FEATURES = [
  {
    icon: <HealthAndSafetyRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    id: "careStart",
    title: "CareStart",
    descriptionKey: "onboarding.features.careStart",
  },
  {
    icon: <SchoolRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    id: "medicalLibrary",
    titleKey: "onboarding.features.medicalLibraryTitle",
    descriptionKey: "onboarding.features.medicalLibrary",
  },
  {
    icon: <FolderSharedRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    id: "masterChart",
    titleKey: "onboarding.features.masterChartTitle",
    descriptionKey: "onboarding.features.masterChart",
  },
];

const FeatureRow = ({ icon, title, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      textAlign: "left",
    }}
  >
    {icon}
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const PatientOnboardingDialog = ({ open, onClose, userId }) => {
  const { t } = useTranslation("home");
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    onClose();

    if (dontShowAgain && userId) {
      db.collection("users")
        .doc(userId)
        .update({ hideOnboardingDialog: true })
        .catch((error) => {
          console.error("Error saving onboarding preference:", error);
        });
    }
  };

  const steps = [
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <HealthAndSafetyRounded color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            {t("onboarding.welcomeTitle")}
          </Typography>
          <Typography variant="body1" paragraph>
            {t("onboarding.intro1")}
          </Typography>
          <Typography variant="body1" paragraph>
            {t("onboarding.intro2")}
          </Typography>
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {t("onboarding.tourTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t("onboarding.tourSubtitle")}
          </Typography>
          <HelpVideo
            src={patientVideo}
            captions={patientCaptions}
            poster={patientPoster}
            caption={t("onboarding.tourCaption")}
          />
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            {t("onboarding.featuresTitle")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {FEATURES.map((feature) => (
              <FeatureRow
                key={feature.id}
                icon={feature.icon}
                title={feature.titleKey ? t(feature.titleKey) : feature.title}
                description={t(feature.descriptionKey)}
              />
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxWidth: 680 },
      }}
    >
      <IconButton
        aria-label={t("common.closeAria")}
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 4, pb: 1 }}>
        {steps[activeStep].content}

        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            background: "transparent",
            justifyContent: "center",
            mt: 2,
          }}
          nextButton={null}
          backButton={null}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label={t("onboarding.dontShowAgain")}
        />
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              {t("common.back")}
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button variant="contained" onClick={handleClose}>
              {t("onboarding.getStarted")}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              {t("common.next")}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PatientOnboardingDialog;
